import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Shield, AlertTriangle, Lightbulb, BarChart3, Heart, User, ArrowRight, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

const InsurancePage = () => {
  const benefits = [
    {
      icon: Shield,
      title: "Protection Against Unpredictable Events",
      description: "Life is unpredictable. Insurance ensures you're not financially derailed by:",
      points: [
        "Sudden health issues or hospitalization",
        "Accidents or permanent disability",
        "Loss of income due to unforeseen events"
      ]
    },
    {
      icon: Lightbulb,
      title: "Tailored to Life Stage & Needs",
      description: "One-size-fits-all doesn't work for insurance. Your strategy should evolve with:",
      points: [
        "Your income level and dependents",
        "Stage of life: single, married, with kids, nearing retirement",
        "Changing financial obligations and lifestyle"
      ]
    },
    {
      icon: BarChart3,
      title: "A Smart Mix of Term, Health & Asset-Linked Covers",
      description: "Don't just pick the cheapest or flashiest plan. We help you create a diversified safety net using:",
      points: [
        "Term Insurance for income replacement",
        "Health Insurance with top-ups for major illnesses",
        "ULIPs or asset-linked plans for future wealth creation + protection"
      ]
    }
  ];

  const calculators = [
    {
      icon: User,
      title: "Human Life Value",
      color: "from-purple-500 to-pink-500",
      features: ["Insurance Cover: Set the right protection", "Dependents' Needs: Secure their future", "Income Loss: Plan for life's uncertainties"],
      link: "/calculators"
    },
    {
      icon: Heart,
      title: "Needs Based Value",
      color: "from-red-500 to-orange-500",
      features: ["Future Goals: Cover key life expenses", "Monthly Income: Ensure post-death support", "Debt Cover: Clear loans if anything happens"],
      link: "/calculators"
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 rounded-full border border-white/10 mb-6">
                <Shield className="w-4 h-4 text-purple-400" />
                <span className="text-white/60 text-sm">Insurance Plans</span>
              </div>

              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Why Do You Need an Insurance Strategy?
              </h1>

              <p className="text-lg text-white/60 leading-relaxed mb-6">
                Insurance is not just a product — it's a strategy. It's your safety net when life throws curveballs. From medical emergencies to untimely loss of income, a well-designed insurance plan shields your family from financial disruption.
              </p>

              <p className="text-white/80 mb-8">
                At <span className="text-purple-400 font-semibold">Frontier Investments</span>, we help you build an insurance strategy that fits your life goals, your stage of life, and the risks unique to your lifestyle — so that peace of mind becomes part of your portfolio.
              </p>

              <Link
                to="/contact"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-black font-semibold rounded-full hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-all duration-500 group"
              >
                Get Protected
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop"
                alt="Insurance Planning"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div
                  key={index}
                  className="p-8 bg-gray-50 rounded-3xl border border-gray-100 hover:shadow-xl transition-all duration-500"
                >
                  <div className="p-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl w-fit mb-6">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-black mb-4">{benefit.title}</h3>
                  <p className="text-gray-600 mb-6">{benefit.description}</p>
                  <ul className="space-y-3">
                    {benefit.points.map((point, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <div className="p-1 bg-purple-100 rounded-full mt-1">
                          <Check className="w-3 h-3 text-purple-600" />
                        </div>
                        <span className="text-gray-600 text-sm">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Calculators Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Plan Your Insurance With Our Calculators
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Calculate the right insurance coverage for your needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            {calculators.map((calc, index) => {
              const IconComponent = calc.icon;
              return (
                <Link
                  key={index}
                  to={calc.link}
                  className="group p-8 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:bg-white/10 transition-all duration-500"
                >
                  <div className={`p-4 bg-gradient-to-br ${calc.color} rounded-xl w-fit mb-6`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{calc.title}</h3>
                  <ul className="space-y-3">
                    {calc.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                        <span className="text-white/60 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6">
            Ready to protect your family's future?
          </h2>
          <p className="text-gray-500 mb-10">
            Let our experts help you create the perfect insurance strategy
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-black text-white font-semibold rounded-full hover:shadow-lg transition-all duration-500 group"
          >
            Book a Consultation
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default InsurancePage;
